import React, { useState, useEffect, useCallback } from "react";
import { MessageCircle } from "lucide-react";

/**
 * Botão flutuante para contato via WhatsApp
 * Posicionado ao lado do botão ScrollToTop
 *
 * @param {string} phoneNumber - Número do WhatsApp (com código do país)
 * @param {string} message - Mensagem padrão a ser enviada
 * @param {number} showAfterScroll - Pixels de scroll antes de mostrar o botão (padrão: 400)
 */
export const WhatsAppButton = ({
  phoneNumber = "5511991882938",
  message = "Olá! Gostaria de fazer um pedido pelo Bonfim Gourmet. Poderia me ajudar?",
  showAfterScroll = 400,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  /**
   * Handler para abrir WhatsApp
   */
  const openWhatsApp = useCallback(() => {
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  }, [phoneNumber, message]);

  /**
   * Monitora o scroll da página para mostrar/ocultar o botão
   */
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > showAfterScroll) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    // Adiciona listener de scroll com throttle passivo
    window.addEventListener("scroll", toggleVisibility, { passive: true });

    // Verifica posição inicial
    toggleVisibility();

    return () => {
      window.removeEventListener("scroll", toggleVisibility);
    };
  }, [showAfterScroll]);

  // Suporte para teclado
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openWhatsApp();
      }
    },
    [openWhatsApp]
  );

  return (
    <button
      onClick={openWhatsApp}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label="Falar com atendimento via WhatsApp"
      className={`fixed fab-slot-1 right-4 sm:right-5 z-50 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl shadow-lg transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-400/50 group ${
        isVisible
          ? "opacity-100 translate-y-0 scale-100"
          : "opacity-0 translate-y-10 scale-90 pointer-events-none"
      }`}
    >
      <MessageCircle className="w-6 h-6" />
      <span className="sr-only">Falar com atendimento via WhatsApp</span>

      {/* Tooltip que aparece ao passar o mouse */}
      <span
        className={`absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap transition-all duration-200 ${
          isHovered
            ? "opacity-100 translate-x-0"
            : "opacity-0 translate-x-2 pointer-events-none"
        }`}
        style={{
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        Fale conosco via WhatsApp
        {/* Seta do tooltip */}
        <span className="absolute left-full top-1/2 -translate-y-1/2 border-8 border-transparent border-l-gray-900"></span>
      </span>
    </button>
  );
};
